// lib/modelist/streetview.ts
// Street View of a 3D model (owner, 2026-10-12, for Compagnie de Phalsbourg's leasing visits): at each point the
// model is rendered as one 360° panorama (equirectangular, 2:1) around a person's eyes, and an image model makes that
// one panorama photographic. Eight overlapping photographs blended into one doubled every wall: each came back framed
// a little differently, so neighbours never lined up (Valvert, 2026-10-12).

import type { StreetPoint } from '#types/types';

/** A point as the server stores it: its photograph a stored file, or none and the canvas workspace it is drawn from. */
export const isStreetPoint = (p: unknown): p is StreetPoint => {
  const v = p as StreetPoint;
  const triple = (a: unknown) => Array.isArray(a) && a.length === 3 && a.every((n) => Number.isFinite(n) && Math.abs(n) < 1e5);
  const stored = (r: unknown) => typeof r === 'string' && r.startsWith('/api/') && r.length < 600;
  const workspace = v?.workspaceId === undefined ? null : typeof v.workspaceId === 'string' && /^[\w-]{1,64}$/.test(v.workspaceId);
  return !!v && typeof v.id === 'string' && /^[\w-]{6,64}$/.test(v.id) && typeof v.name === 'string' && v.name.length <= 80 && triple(v.feet)
    && Number.isFinite(v.yaw) && workspace !== false && (v.pano === undefined ? workspace === true : stored(v.pano))
    && [v.flat, v.depth].every((r) => r === undefined || stored(r));
};

/** A person's eyes above the ground, in metres. */
export const STREET_EYE = 1.6;
/** The panorama rendered from the model: 8K so a small brand logo is legible to the sign check; the image model gets a 4K copy. */
export const PANO_SIZE = { width: 8192, height: 4096 };
/** A canvas workspace point's 360°, as three latitude bands each at its own width for the full 360°: the horizon
 * carries the detail, the sky and the near ground do not. Horizon last: its layer sits above the other two. */
export const STREET_BANDS = [
  { name: 'sky', lat: [35, 90], width: 2048 },
  { name: 'ground', lat: [-90, -35], width: 2048 },
  { name: 'horizon', lat: [-35, 35], width: 12288 },
] as const;
/** Degrees a band's layer reaches past its edges, so no hairline gap shows between the layers on the canvas. */
export const STREET_BAND_OVERLAP = 1;
// The horizon's layers on the canvas: tiles of this width (owner, 2026-10-14), overlapping by a few pixels.
export const STREET_TILE_WIDTH = 4096;
export const STREET_TILE_OVERLAP_PX = 8;
/** A band's rows for the latitudes it spans: the whole equirectangular image is width x width / 2. */
export const bandHeight = (width: number, [from, to]: readonly [number, number]) => Math.round((width * (to - from)) / 360);
/** The latitudes a band's layer holds: its own and the overlap, within the poles. */
export const bandCaptured = ([from, to]: readonly [number, number]): [number, number] =>
  [Math.max(-90, from - STREET_BAND_OVERLAP), Math.min(90, to + STREET_BAND_OVERLAP)];

/** The direction of a panorama pixel (u, v from the top left): longitude 0 looks down -Z, x to the right, y up. */
export function panoDirection(u: number, v: number): [number, number, number] {
  const lon = (u - 0.5) * 2 * Math.PI, lat = (0.5 - v) * Math.PI;
  return [Math.sin(lon) * Math.cos(lat), Math.sin(lat), -Math.cos(lon) * Math.cos(lat)];
}

// Brand names are compared word for word and counted, not judged: the photograph's signs are read the way the
// render's were. Reading where a sign sits proved too noisy to compare; its count catches a brand copied onto another shop.
export const signKey = (s: string) => s.normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim();
export function signsCheck(expected: string[], found: string[]) {
  const left = expected.map(signKey);
  const extra = found.filter((f) => {
    const i = left.indexOf(signKey(f));
    if (i >= 0) left.splice(i, 1);
    return i < 0;
  });
  // The photograph is sharper than the render, so a brand legible on the render and not on the photograph was garbled
  // (Valvert: Façonnable came back as "Eagnmolo" and read as merely missing). Garbled lettering is read as "?".
  return { ok: extra.length === 0 && left.length === 0, extra, missing: left };
}
